import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { generateSHA256 } from "../util/generateSHA256";
import { deleteElement } from "../redux/sectionSlice";
import { getAuditTrailData, extractVersionStructure } from "../util/getAuditTrailData";

export const useDeleteFunctionality = (locationState) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const {
    partId,
    revisionId,
    fromSectionId,
    fromStepId,
    fromSubstepId,
    fromNodeId,
    revision,
    partNumber,
    type,
    description,
    status,
    title,
    elementType,
    move_rev_node_info,
  } = locationState;

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const [auditData, setAuditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    hash: generateSHA256(Math.random().toString()),
    signature: "",
  });

  // Fetch IP address
  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) =>
        setAuditData((prev) => ({ ...prev, ip_address: data.ip }))
      )
      .catch((err) => console.error("Failed to fetch IP:", err));
  }, []);

  // Snackbar functions
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Modal functions
  const handleConfirm = () => {
    setConfirmModalOpen(true);
  };

  const handleConfirmSave = async (deleteType, allRevisionNodes, filteredContent) => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    setIsLoading(true);

    let newContent = structuredClone(allRevisionNodes);
    
    // Apply deletion logic based on type
    switch (deleteType) {
      case "SECTION":
        newContent = newContent.filter(
          (sec) => sec.section_id_ref !== fromSectionId
        );
        break;
      case "STEP":
        newContent = newContent.map((section) => {
          if (section.section_id_ref === fromSectionId) {
            section.steps = section.steps.filter(
              (step) => step.step_id_ref !== fromStepId
            );
          }
          return section;
        });
        break;
      case "SUBSTEP":
        newContent = newContent.map((section) => {
          if (section.section_id_ref === fromSectionId) {
            section.steps = section.steps.map((step) => {
              if (step.step_id_ref === fromStepId) {
                step.substeps = step.substeps.filter(
                  (substep) => substep.substep_id_ref !== fromSubstepId
                );
              }
              return step;
            });
          }
          return section;
        });
        break;
      case "CHILD":
        newContent = newContent.map((section) => {
          if (section.section_id_ref === fromSectionId) {
            let keyToCheck = "";
            if (elementType === "attachment") keyToCheck = "attachment_tree_node_id";
            else if (elementType === "video") keyToCheck = "video_tree_node_id";
            else if (elementType === "rich-text") keyToCheck = "rich_text_node_id";
            else if (elementType === "canvas") keyToCheck = "canvas_tree_node_id";

            section.steps = section.steps.map((step) => {
              if (step.step_id_ref === fromStepId) {
                step.children = step.children.filter(
                  (child) => child.detail[keyToCheck] !== fromNodeId
                );
                if (fromSubstepId !== null && fromSubstepId !== "null") {
                  step.substeps = step.substeps.map((substep) => {
                    if (substep.substep_id_ref === fromSubstepId) {
                      substep.children = substep.children.filter(
                        (child) => child.detail[keyToCheck] !== fromNodeId
                      );
                    }
                    return substep;
                  });
                }
              }
              return step;
            });
          }
          return section;
        });
        break;
      default:
        newContent = filteredContent || newContent;
    }

    // Generate audit trail data
    const actionType = deleteType === "SECTION" ? "SECTION_NODE_DELETED" :
                      deleteType === "STEP" ? "STEP_NODE_DELETED" :
                      deleteType === "SUBSTEP" ? "SUBSTEP_NODE_DELETED" :
                      `${elementType?.toUpperCase()}_NODE_DELETED`;

    const actionToShow = deleteType === "SECTION" ? 
      `Deleted ${title} in ${partNumber}, Rev ${revision}` :
      deleteType === "STEP" ?
      `Deleted ${move_rev_node_info?.fromStep || title} from ${move_rev_node_info?.fromSection || ""} in ${partNumber}, Rev ${revision}` :
      deleteType === "SUBSTEP" ?
      `Deleted ${move_rev_node_info?.fromSubStep || title} from ${move_rev_node_info?.fromSection || ""} > ${move_rev_node_info?.fromStep || ""} in ${partNumber}, Rev ${revision}` :
      `Deleted ${move_rev_node_info?.fromNodeHeading || title} from ${move_rev_node_info?.fromSection || ""} > ${move_rev_node_info?.fromStep || ""}${
        move_rev_node_info?.fromSubStep !== null ? " > " + move_rev_node_info.fromSubStep : ""
      } in ${partNumber}, Rev ${revision}`;

    const audit_trail_data = await getAuditTrailData(
      {
        action: actionType,
        comment: comment,
        version_content: {
          action_to_show: actionToShow,
          originingalContent: extractVersionStructure(allRevisionNodes),
          newContent: extractVersionStructure(newContent),
          rev: revision,
          status,
          item_code_type: type,
          partNumber,
        },
      },
      false
    );

    // Create payload
    const payload = {
      part_id_ref: partId,
      procedure_rev_ref: revisionId,
      section_id_ref: fromSectionId,
      step_id_ref: fromStepId || "",
      substep_id_ref: (fromSubstepId === "null" || fromSubstepId === null) ? "" : fromSubstepId,
      child_node_ref: fromNodeId || "",
      audit_trail_data,
    };

    try {
      await dispatch(deleteElement(payload)).unwrap();
      setModalOpen(false);
      navigate(
        `/revision-submission/${partId}/${revision}/${type}/${partNumber}/${revisionId}/${encodeURIComponent(
          description || ""
        )}`,
        {
          replace: true,
          state: { status },
        }
      );
    } catch (error) {
      console.error(`Error deleting ${deleteType.toLowerCase()}:`, error);
      showSnackbar(`Error deleting ${title}: ` + error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // State
    isLoading,
    isModalOpen,
    isConfirmModalOpen,
    comment,
    snackbar,
    
    // Setters
    setModalOpen,
    setConfirmModalOpen,
    setComment,
    setIsLoading,
    
    // Functions
    showSnackbar,
    handleCloseSnackbar,
    handleConfirm,
    handleConfirmSave,
  };
};

export default useDeleteFunctionality;
